import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
// eslint-disable-next-line boundaries/element-types
import { TellentTwoFactorAuthenticationActions } from '@core/tellent-data'; // FIXME
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { DialogService } from '@core/user-interface-legacy';

import { Providers } from './current-user-profile-security.component';

@Injectable({ providedIn: 'root' })
export class CurrentUserProfileSecurityDialogService {
  private readonly dialogService = inject(DialogService);
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);

  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  public isOrgOwner = this.currentUserFacade.isOrgOwner;
  public currentUser = this.currentUserFacade.currentUser;
  public currentOrg = this.currentUserFacade.organizationMembershipSafe;

  private translations = {
    twoFa_confirm_dialog: {
      message: this.i18n.translate(
        'tlc.current_user.dialog_2fa_sms.disable_sms_confirm_dialog.message',
      ),
      title: this.i18n.translate(
        'tlc.current_user.dialog_2fa_sms.disable_sms_confirm_dialog.title',
      ),
      agreeLabel: this.i18n.translate(
        'tlc.current_user.dialog_2fa_sms.disable_sms_confirm_dialog.agree_label',
      ),
    },
    delete_confirm_dialog: {
      message_header: this.i18n.translate(
        'tlc.current_user.delete_account.confirm_delete_dialog.message_header',
        {
          email: this.currentUser()?.email,
          accountsNumber: this.currentUser()?.members.length,
          companies: this.currentUser()
            ?.members.map(member => member.org.name)
            .join(', '),
        },
      ),
      message_body: this.i18n.translate(
        'tlc.current_user.delete_account.confirm_delete_dialog.message_body',
      ),
      title: this.i18n.translate('tlc.current_user.delete_account.confirm_delete_dialog.title'),
      agreeLabel: this.i18n.translate(
        'tlc.current_user.delete_account.confirm_delete_dialog.agree_label',
      ),
      checkboxLabel: this.i18n.translate(
        'tlc.current_user.delete_account.confirm_delete_dialog.checkbox_label',
      ),
    },
    is_owner_dialog: {
      message: this.i18n.translate('tlc.current_user.delete_account.is_owner_dialog.message', {
        companyName: this.currentOrg()?.org.name,
      }),
      title: this.i18n.translate('tlc.current_user.delete_account.is_owner_dialog.title'),
      cancelLabel: this.i18n.translate(
        'tlc.current_user.delete_account.is_owner_dialog.cancel_label',
      ),
    },
  };

  public showConfirmation2FaDialog(emailCode: string | null, provider: Providers): void {
    this.dialogService
      .confirm({
        message: this.translations.twoFa_confirm_dialog.message,
        title: this.translations.twoFa_confirm_dialog.title,
        variant: 'warning',
        safeFooter: false,
        isCancelable: true,
        agreeLabel: this.translations.twoFa_confirm_dialog.agreeLabel,
        confirmActionsLayout: 'end',
      })
      .pipe(take(1), filterBoolean)
      .subscribe(() => {
        if (provider === Providers.App) {
          this.store.dispatch(
            TellentTwoFactorAuthenticationActions.disableAppAuth({
              payload: emailCode,
            }),
          );
        }
        if (provider === Providers.Sms) {
          this.store.dispatch(
            TellentTwoFactorAuthenticationActions.disableSmsAuth({ payload: emailCode }),
          );
        }
      });
  }

  public openConfirmDeleteDialog(emailCode: string): void {
    const message =
      this.translations.delete_confirm_dialog.message_header +
      this.translations.delete_confirm_dialog.message_body;
    this.dialogService
      .confirm({
        message,
        title: this.translations.delete_confirm_dialog.title,
        variant: 'error',
        safeFooter: false,
        isCancelable: true,
        agreeLabel: this.translations.delete_confirm_dialog.agreeLabel,
        confirmActionsLayout: 'space-between',
        checkboxes: [
          {
            title: this.translations.delete_confirm_dialog.checkboxLabel,
            value: false,
          },
        ],
      })
      .pipe(take(1), filterBoolean)
      .subscribe(() => {
        this.currentUserFacade.deleteAccount(emailCode);
      });
  }

  public openIsOwnerDialog() {
    this.dialogService.confirm({
      message: this.translations.is_owner_dialog.message,
      title: this.translations.is_owner_dialog.title,
      variant: 'warning',
      safeFooter: false,
      isCancelable: true,
      agreeLabel: '',
      cancelLabel: this.translations.is_owner_dialog.cancelLabel,
      confirmActionsLayout: 'end',
    });
  }
}
